import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, KeyRound, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { adminChangePassword } from "@/lib/api";

export default function ChangePasswordDialog({
  open,
  onOpenChange,
  currentPassword,
  onPasswordChanged,
}) {
  const [current, setCurrent] = useState(currentPassword || "");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showNext, setShowNext] = useState(false);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setShowNext(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!current) {
      toast.error("Enter your current password.");
      return;
    }
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation don't match.");
      return;
    }
    if (next === current) {
      toast.error("New password must be different from current.");
      return;
    }
    try {
      setLoading(true);
      await adminChangePassword(current, next);
      toast.success("Password changed! You'll need to sign in again.");
      onPasswordChanged?.(next);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not update password."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setTimeout(reset, 250);
      }}
    >
      <DialogContent
        data-testid="change-password-dialog"
        className="bg-[#0a0a0a] border-[#27272A] text-white sm:max-w-[480px]"
      >
        <DialogHeader>
          <div className="w-12 h-12 mx-auto rounded-xl bg-[#CCFF00]/10 border border-[#CCFF00]/30 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-[#CCFF00]" />
          </div>
          <DialogTitle className="font-display text-2xl font-black tracking-tight text-center">
            Change admin password
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-center">
            Pick something only you’ll remember. Min 6 characters.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-jb text-zinc-400">
              Current Password
            </Label>
            <Input
              data-testid="change-pw-current"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00]"
              required
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-jb text-zinc-400">
              New Password
            </Label>
            <div className="relative">
              <Input
                data-testid="change-pw-new"
                type={showNext ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00] pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowNext(!showNext)}
                className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5 text-zinc-500 hover:text-[#CCFF00]"
                tabIndex={-1}
              >
                {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-jb text-zinc-400">
              Confirm New Password
            </Label>
            <Input
              data-testid="change-pw-confirm"
              type={showNext ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00]"
              required
            />
          </div>

          <div className="bg-[#111] border border-[#27272A] rounded-lg p-3 flex items-start gap-2 text-xs text-zinc-400">
            <ShieldCheck className="w-4 h-4 text-[#CCFF00] shrink-0 mt-0.5" />
            <p>
              You’ll be signed out after the change. Sign in again with your new password.
            </p>
          </div>

          <Button
            type="submit"
            data-testid="change-pw-submit"
            disabled={loading}
            className="btn-neon rounded-none w-full h-11"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update Password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
